import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MaterialModule } from '@fullyops/shared/material.module';
import { DateTimeInputComponent } from '../date-time-input/date-time-input.component';
import moment from 'moment-timezone';
import { Moment } from 'moment-timezone';
import { I18NextModule } from 'angular-i18next';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { Subscription } from 'rxjs';
import {
  MAT_MOMENT_DATE_FORMATS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { ZonedMomentDateAdapter } from '@fullyops/shared/zoned-moment-date-adapter';
import { addDateRangeValidation } from '@fullyops/shared/date-range-validation';
import { setLocalTimeMinutes } from '@fullyops/shared/local-time-minutes';

export interface DateTimeRange {
  allDay: boolean;
  start: Moment | null;
  end: Moment | null;
}

/**
 * A validator that requires both the start and end attributes of a DateTimeRange to be non-null.
 * If applied to a fo-date-time-range-input, all of its date fields will be required as well.
 */
export function bothTimeRangeBoundsRequired(
  ctl: AbstractControl<DateTimeRange>,
): ValidationErrors | null {
  if (ctl.value.start != null && ctl.value.end != null) {
    return null;
  }
  return { missingBounds: true };
}

@Component({
  selector: 'fo-date-time-range-input',
  standalone: true,
  templateUrl: './date-time-range-input.component.html',
  styleUrl: './date-time-range-input.component.scss',
  imports: [
    MaterialModule,
    ReactiveFormsModule,
    I18NextModule,
    DateTimeInputComponent,
  ],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    // MomentDateAdapter isn't providedIn: root, so we must inject it here so
    // that ZonedMomentDateAdapter can delegate to it.
    MomentDateAdapter,
    // Since we're not using MomentDateAdapter directly, we must specify the
    // date formats ourselves.
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    { provide: DateAdapter, useClass: ZonedMomentDateAdapter },
  ],
})
export class DateTimeRangeInputComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @Input() defaultDurationMinutes?: number;
  @Input() minTimeMinutes = 0;
  @Input() maxTimeMinutes = 1440;

  protected allDayControl = new FormControl(false, { nonNullable: true });
  protected startDateControl = new FormControl<Moment | null>(null);
  protected endDateControl = new FormControl<Moment | null>(null);
  protected singularDateControl = new FormControl<Moment | null>(null);

  private onChange = (v: DateTimeRange) => {};
  private onTouched = () => {};

  // implementation of ControlValueAccessor for use with reactive forms
  writeValue(v: DateTimeRange): void {
    this.allDayControl.setValue(v.allDay, { emitEvent: false });
    this.startDateControl.setValue(v.start, { emitEvent: false });
    this.endDateControl.setValue(v.end, { emitEvent: false });
    this.singularDateControl.setValue(v.start, { emitEvent: false });
  }
  registerOnChange(f: (v: DateTimeRange) => void): void {
    this.onChange = f;
  }

  private emitValue(): void {
    // Ensure all internal controls are up-to-date with their validation
    this.allDayControl.updateValueAndValidity({ emitEvent: false });
    this.startDateControl.updateValueAndValidity({ emitEvent: false });
    this.endDateControl.updateValueAndValidity({ emitEvent: false });

    if (
      this.allDayControl.valid &&
      this.startDateControl.valid &&
      this.endDateControl.valid
    ) {
      this.onChange({
        allDay: this.allDayControl.value,
        start: this.startDateControl.value,
        end: this.endDateControl.value,
      });
    }

    this.onTouched();

    // Always update parent validation regardless of internal validity
    if (this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity({ emitEvent: false });
    }
  }

  registerOnTouched(f: () => void): void {
    this.onTouched = f;
  }
  setDisabledState(isDisabled: boolean): void {
    const controls = [
      this.allDayControl,
      this.startDateControl,
      this.endDateControl,
      this.singularDateControl,
    ];
    if (isDisabled) {
      for (var c of controls) {
        c.disable({ emitEvent: false });
      }
    } else {
      for (var c of controls) {
        c.enable({ emitEvent: false });
      }
    }
  }

  private sub = new Subscription();

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (ngControl != null) {
      ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.sub.add(
      this.allDayControl.valueChanges.subscribe((allDay) => {
        if (allDay) {
          if (this.startDateControl.value == null) {
            this.singularDateControl.setValue(null, { emitEvent: false });
          } else {
            const startOfDay = this.startDateControl.value
              .clone()
              .startOf('day');
            this.singularDateControl.setValue(startOfDay, { emitEvent: false });
            this.setAllDayRange(startOfDay);
          }
        }
        this.emitValue();
      }),
    );
    this.sub.add(
      this.singularDateControl.valueChanges.subscribe((d) => {
        if (d == null) {
          this.startDateControl.setValue(null, { emitEvent: false });
          this.endDateControl.setValue(null, { emitEvent: false });
        } else {
          this.setAllDayRange(d);
        }
        this.emitValue();
      }),
    );
    this.sub.add(
      this.startDateControl.valueChanges.subscribe((startDate) => {
        if (
          startDate != null &&
          this.defaultDurationMinutes != null &&
          this.endDateControl.value == null
        ) {
          // startDate may be a string sometimes when coming from legacy code.
          this.endDateControl.setValue(
            moment(startDate).add(this.defaultDurationMinutes, 'minutes'),
            { emitEvent: false },
          );
        }
        this.emitValue();
      }),
    );
    this.sub.add(
      this.endDateControl.valueChanges.subscribe(() => this.emitValue()),
    );
    // Without the explicit type annotation, TypeScript infers the wrong type for the parameters
    // and makes the call invalid.
    this.sub.add(
      addDateRangeValidation<Moment>(
        this.startDateControl,
        this.endDateControl,
      ),
    );
    const isFullyRequired = this.ngControl.control.hasValidator(
      bothTimeRangeBoundsRequired,
    );
    if (isFullyRequired) {
      this.startDateControl.addValidators(Validators.required);
      this.endDateControl.addValidators(Validators.required);
      this.singularDateControl.addValidators(Validators.required);
    }
  }

  private setAllDayRange(startOfDay: Moment): void {
    this.startDateControl.setValue(
      setLocalTimeMinutes(startOfDay, this.minTimeMinutes),
      { emitEvent: false },
    );
    this.endDateControl.setValue(
      setLocalTimeMinutes(startOfDay, this.maxTimeMinutes),
      { emitEvent: false },
    );
    // When moving the date forward, setting the start date control's value leaves it invalid
    // as it is temporarily ahead of the end date control.
    // Due to the use of emitEvent: false to prevent a value from being emitted from this component
    // too early, the handlers in addDateRangeValidation are not triggered after the end date is
    // set and thus the start date control is not re-validated as it would be if the user had
    // been manipulating these fields manually.
    // Ideally we would dispense with this kludgey way of validating date ranges, but until then
    // we'll just re-check the start date control's validation status after the end date is set.
    this.startDateControl.updateValueAndValidity({ emitEvent: false });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
